import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { User, Trophy, Star, Award, Calendar, Heart, School, Target, Edit, Briefcase, Flame, Camera, ChevronRight, ArrowLeft, Bell, BellOff } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import ProfileCompletionModal from '@/components/ProfileCompletionModal';
import { useTheme } from '@/contexts/ThemeContext';
import { subscribeToPush, unsubscribeFromPush, getPushStatus } from '@/utils/pushNotifications';
import { formatDisplayName } from '@/utils/formatText';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_COLORS = {
  bronze: 'bg-amber-600',
  silver: 'bg-gray-400',
  gold: 'bg-yellow-500',
  diamond: 'bg-cyan-500'
};

const TIER_ORDER = { diamond: 0, gold: 1, silver: 2, bronze: 3 };

const Profile = ({ user, setUser }) => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const [checkins, setCheckins] = useState([]);
  const [badges, setBadges] = useState({ earned: [], total_available: 0 });
  const [accomplishments, setAccomplishments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [pushStatus, setPushStatus] = useState({ supported: false, enabled: false });
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
    checkPushStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkPushStatus = async () => {
    const status = await getPushStatus();
    setPushStatus(status);
  };

  const togglePush = async () => {
    if (pushStatus.enabled) {
      await unsubscribeFromPush();
      setPushStatus(prev => ({ ...prev, enabled: false }));
      toast.success('Push notifications disabled');
    } else {
      const sub = await subscribeToPush();
      if (sub) {
        setPushStatus(prev => ({ ...prev, enabled: true }));
        toast.success('Push notifications enabled!');
      } else {
        toast.error('Could not enable push notifications. Please allow notifications in your browser settings.');
      }
    }
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [checkinsRes, badgesRes, accomplishmentsRes] = await Promise.all([
        fetch(`${API_URL}/api/checkins/my`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/badges/my`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/accomplishments`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      if (checkinsRes.ok) setCheckins(await checkinsRes.json());
      if (badgesRes.ok) setBadges(await badgesRes.json());
      if (accomplishmentsRes.ok) setAccomplishments(await accomplishmentsRes.json());
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB');
      return;
    }

    setUploadingPhoto(true);

    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64 = event.target?.result;
        
        // Resize image if needed
        const img = new Image();
        img.onload = async () => {
          const canvas = document.createElement('canvas');
          const maxSize = 300;
          let width = img.width;
          let height = img.height;
          
          if (width > height) {
            if (width > maxSize) {
              height *= maxSize / width;
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width *= maxSize / height;
              height = maxSize;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          const resizedBase64 = canvas.toDataURL('image/jpeg', 0.8);
          
          // Upload to backend
          const response = await fetch(`${API_URL}/api/users/me`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({ profile_picture: resizedBase64 })
          });

          if (response.ok) {
            const updatedUser = await response.json();
            setUser(updatedUser);
            toast.success('Profile picture updated! 📸');
          } else {
            toast.error('Failed to update profile picture');
          }
          setUploadingPhoto(false);
        };
        img.src = base64;
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to upload photo');
      setUploadingPhoto(false);
    }
  };

  // Sort badges by tier (diamond first)
  const sortedBadges = [...(badges.earned || [])].sort((a, b) => 
    TIER_ORDER[a.tier] - TIER_ORDER[b.tier]
  );

  return (
    <div className="min-h-screen pb-24" style={{ backgroundColor: colors?.background || '#FEF9F3' }}>
      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handlePhotoUpload}
        accept="image/*"
        className="hidden"
        data-testid="profile-picture-input"
      />

      {/* Profile Header */}
      <div className="p-6 md:p-8 rounded-b-3xl shadow-lg mb-6" style={{ background: colors?.header_gradient || colors?.header || '#1B2D4F' }}>
        <div className="max-w-4xl mx-auto">
          <button onClick={() => navigate(-1)} className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors mb-3" data-testid="back-btn"><ArrowLeft className="w-5 h-5 text-white" /></button>
          <div className="flex flex-col items-center text-center" style={{ color: 'white' }}>
            <div className="relative">
              <Avatar className="w-24 h-24 mb-4 ring-4 ring-white/30">
                {user.profile_picture ? (
                  <AvatarImage src={user.profile_picture} alt={user.name} />
                ) : null}
                <AvatarFallback className="bg-white/20 text-4xl font-bold" style={{ color: 'white', fontFamily: 'var(--font-heading)' }}>
                  {user.avatar_emoji || user.name.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPhoto}
                className="absolute bottom-3 right-0 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg hover:bg-gray-100 transition-colors"
                data-testid="upload-photo-btn"
              >
                {uploadingPhoto ? (
                  <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Camera className="w-4 h-4 text-gray-700" />
                )}
              </button>
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mb-2" style={{ color: 'white' }}>{user.name}</h1>
            <p className="text-lg mb-2" style={{ color: 'rgba(255,255,255,0.9)' }}>{user.email}</p>
            {user.secondary_email && (
              <p className="text-sm mb-2" style={{ color: 'rgba(255,255,255,0.75)' }}>{user.secondary_email}</p>
            )}
            {user.work_site && (
              <p className="text-sm bg-white/20 px-4 py-2 rounded-full flex items-center gap-2" style={{ color: 'rgba(255,255,255,0.85)' }}>
                <Briefcase className="w-4 h-4" />
                {user.work_site}
              </p>
            )}
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-3 gap-3 mt-6">
            <div data-testid="total-points-stat" className="bg-white/20 backdrop-blur-lg rounded-2xl p-4 text-center">
              <Trophy className="w-7 h-7 mx-auto mb-1 text-yellow-300" />
              <p className="text-2xl font-heading font-bold" style={{ color: 'white' }}>{user.total_points}</p>
              <p className="text-xs" style={{ color: 'rgba(255,255,255,0.9)' }}>Points</p>
            </div>
            <div data-testid="badges-stat" className="bg-white/20 backdrop-blur-lg rounded-2xl p-4 text-center">
              <Award className="w-7 h-7 mx-auto mb-1 text-cyan-300" />
              <p className="text-2xl font-heading font-bold" style={{ color: 'white' }}>{badges.total_earned || 0}</p>
              <p className="text-xs" style={{ color: 'rgba(255,255,255,0.9)' }}>Badges</p>
            </div>
            <div data-testid="streak-stat" className="bg-white/20 backdrop-blur-lg rounded-2xl p-4 text-center">
              <Flame className="w-7 h-7 mx-auto mb-1 text-orange-300" />
              <p className="text-2xl font-heading font-bold" style={{ color: 'white' }}>{user.current_streak || 0}</p>
              <p className="text-xs" style={{ color: 'rgba(255,255,255,0.9)' }}>Day Streak</p>
            </div>
          </div>
        </div>
      </div>

      {/* Profile Details */}
      <div className="max-w-4xl mx-auto px-4 md:px-6 space-y-6">
        {/* Edit Profile Button */}
        <div className="flex justify-end">
          <Button
            data-testid="edit-profile-button"
            onClick={() => setShowEditModal(true)}
            variant="outline"
            className="rounded-full"
          >
            <Edit className="w-4 h-4 mr-2" />
            Edit Profile
          </Button>
        </div>

        {/* Profile Completion Modal */}
        <ProfileCompletionModal 
          user={user} 
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onComplete={(updatedUser) => {
            setUser(updatedUser);
            setShowEditModal(false);
          }} 
        />

        {/* Notification Settings */}
        {pushStatus.supported && (
          <Card className="bg-white rounded-2xl p-4 shadow-card" data-testid="push-notification-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${pushStatus.enabled ? 'bg-green-100' : 'bg-gray-100'}`}>
                  {pushStatus.enabled ? <Bell className="w-5 h-5 text-green-600" /> : <BellOff className="w-5 h-5 text-gray-400" />}
                </div>
                <div>
                  <p className="font-semibold text-sm">Push Notifications</p>
                  <p className="text-xs text-muted-foreground">{pushStatus.enabled ? 'You\'ll get notified about new activity' : 'Enable to stay updated'}</p>
                </div>
              </div>
              <Button 
                onClick={togglePush}
                variant={pushStatus.enabled ? "outline" : "default"}
                size="sm"
                className="rounded-full"
                data-testid="toggle-push-btn"
              >
                {pushStatus.enabled ? 'Disable' : 'Enable'}
              </Button>
            </div>
          </Card>
        )}

        {/* Badges Section */}
        <div data-testid="badges-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-2xl font-bold flex items-center gap-2">
              <Award className="w-6 h-6 text-primary" />
              My Badges
              <Badge variant="outline" className="ml-2 text-sm">
                {badges.total_earned || 0} / {badges.total_available}
              </Badge>
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/badges')}
              className="text-primary"
              data-testid="view-all-badges-btn"
            >
              View All <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          <Card className="bg-white rounded-3xl p-6 shadow-card">
            {sortedBadges.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {sortedBadges.slice(0, 4).map((badge) => (
                  <motion.div
                    key={badge.id}
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    whileHover={{ scale: 1.05 }}
                    className="text-center cursor-pointer"
                    onClick={() => navigate('/badges')}
                  >
                    <div className={`w-16 h-16 mx-auto rounded-full ${TIER_COLORS[badge.tier]} flex items-center justify-center text-3xl shadow-lg mb-2`}>
                      {badge.icon}
                    </div>
                    <p className="font-bold text-sm">{badge.name}</p>
                    <p className="text-xs text-muted-foreground capitalize">{badge.tier}</p>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Award className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-30" />
                <p className="text-muted-foreground">No badges earned yet</p>
                <p className="text-sm text-muted-foreground mt-1">Complete activities to earn badges!</p>
                <Button 
                  onClick={() => navigate('/badges')} 
                  variant="outline" 
                  className="mt-4 rounded-full"
                >
                  See How to Earn Badges
                </Button>
              </div>
            )}
            {sortedBadges.length > 4 && (
              <div className="text-center mt-4 pt-4 border-t">
                <Button 
                  onClick={() => navigate('/badges')} 
                  variant="outline"
                  className="rounded-full"
                >
                  View All {sortedBadges.length} Badges
                </Button>
              </div>
            )}
          </Card>
        </div>

        {/* Accomplishments Section */}
        <div data-testid="accomplishments-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-2xl font-bold flex items-center gap-2">
              <Star className="w-6 h-6 text-purple-500" />
              My Accomplishments
              <Badge variant="outline" className="ml-2 text-sm">
                {accomplishments.length}
              </Badge>
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/accomplishments')}
              className="text-purple-600"
              data-testid="view-all-accomplishments-btn"
            >
              View All <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          <Card className="bg-white rounded-3xl p-6 shadow-card">
            {accomplishments.length > 0 ? (
              <div className="space-y-3">
                {accomplishments.slice(0, 3).map((acc) => (
                  <div 
                    key={acc.id} 
                    className="flex items-start gap-3 bg-purple-50 rounded-xl p-3 cursor-pointer hover:bg-purple-100 transition-colors"
                    onClick={() => navigate('/accomplishments')}
                  >
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center text-gray-900 text-lg flex-shrink-0">
                      🏆
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm truncate">{acc.title}</p>
                      <p className="text-xs text-muted-foreground truncate">{acc.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Star className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-30" />
                <p className="text-muted-foreground">No accomplishments yet</p>
                <p className="text-sm text-muted-foreground mt-1">Share your wins and earn points!</p>
                <Button 
                  onClick={() => navigate('/accomplishments')} 
                  className="mt-4 rounded-full bg-gradient-to-r from-purple-500 to-pink-500"
                >
                  Add Your First Win
                </Button>
              </div>
            )}
            {accomplishments.length > 3 && (
              <div className="text-center mt-4 pt-4 border-t">
                <Button 
                  onClick={() => navigate('/accomplishments')} 
                  variant="outline"
                  className="rounded-full"
                >
                  View All {accomplishments.length} Accomplishments
                </Button>
              </div>
            )}
          </Card>
        </div>

        {/* Career Industries */}
        {user.career_industries?.length > 0 && (
          <div data-testid="career-section">
            <h2 className="font-heading text-2xl font-bold mb-4 flex items-center gap-2">
              <Briefcase className="w-6 h-6 text-blue-500" />
              Career Interests
            </h2>
            <Card className="bg-white rounded-3xl p-6 shadow-card">
              <div className="flex flex-wrap gap-3">
                {user.career_industries.map((industry, idx) => (
                  <Badge key={idx} className="bg-blue-100 text-blue-800 px-4 py-2 text-sm">
                    {industry}
                  </Badge>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* Interests & Favorites */}
        {(user.interests?.length > 0 || user.favorite_food || user.favorite_sport_team || user.favorite_show || user.school) && (
          <div data-testid="interests-section">
            <h2 className="font-heading text-2xl font-bold mb-4 flex items-center gap-2">
              <Heart className="w-6 h-6 text-secondary" />
              About Me
            </h2>
            <Card className="bg-white rounded-3xl p-6 shadow-card space-y-4">
              {user.interests?.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-muted-foreground mb-2">Interests</p>
                  <div className="flex flex-wrap gap-2">
                    {user.interests.map((interest, idx) => (
                      <Badge key={idx} className="bg-primary/10 text-primary px-3 py-1">
                        {formatDisplayName(interest)}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {user.school && (
                <div>
                  <p className="text-sm font-semibold text-muted-foreground mb-1">School</p>
                  <p className="flex items-center gap-2">
                    <School className="w-4 h-4 text-muted-foreground" />
                    {user.school}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {user.favorite_food && (
                  <div>
                    <p className="text-sm font-semibold text-muted-foreground mb-1">Favorite Food</p>
                    <p>🍕 {user.favorite_food}</p>
                  </div>
                )}
                {user.favorite_sport_team && (
                  <div>
                    <p className="text-sm font-semibold text-muted-foreground mb-1">Favorite Team</p>
                    <p>🏀 {user.favorite_sport_team}</p>
                  </div>
                )}
                {user.favorite_show && (
                  <div>
                    <p className="text-sm font-semibold text-muted-foreground mb-1">Favorite Show</p>
                    <p>📺 {user.favorite_show}</p>
                  </div>
                )}
              </div>
            </Card>
          </div>
        )}

        {/* Social Media */}
        <Card className="bg-white rounded-3xl p-6 shadow-card">
          <h3 className="font-heading text-lg font-bold mb-3">Socials</h3>
          {(user.social_tiktok || user.social_instagram || user.social_twitter || user.social_linkedin) ? (
            <div className="flex flex-wrap gap-3">
              {user.social_tiktok && <a href={`https://tiktok.com/@${user.social_tiktok}`} target="_blank" rel="noreferrer" className="px-3 py-1.5 rounded-full bg-gray-100 text-sm hover:bg-gray-200 transition-colors">TikTok: @{user.social_tiktok}</a>}
              {user.social_instagram && <a href={`https://instagram.com/${user.social_instagram}`} target="_blank" rel="noreferrer" className="px-3 py-1.5 rounded-full bg-gray-100 text-sm hover:bg-gray-200 transition-colors">IG: @{user.social_instagram}</a>}
              {user.social_twitter && <a href={`https://x.com/${user.social_twitter}`} target="_blank" rel="noreferrer" className="px-3 py-1.5 rounded-full bg-gray-100 text-sm hover:bg-gray-200 transition-colors">X: @{user.social_twitter}</a>}
              {user.social_linkedin && <a href={`https://linkedin.com/in/${user.social_linkedin}`} target="_blank" rel="noreferrer" className="px-3 py-1.5 rounded-full bg-gray-100 text-sm hover:bg-gray-200 transition-colors">LinkedIn: {user.social_linkedin}</a>}
            </div>
          ) : (
            <button
              onClick={() => setShowEditModal(true)}
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
              data-testid="add-socials-btn"
            >
              + Add your social media handles
            </button>
          )}
        </Card>

        {/* Check-in History */}
        <div data-testid="checkins-section">
          <h2 className="font-heading text-2xl font-bold mb-4 flex items-center gap-2">
            <Calendar className="w-6 h-6 text-green-500" />
            Check-in History
          </h2>
          <Card className="bg-white rounded-3xl p-6 shadow-card">
            {checkins.length > 0 ? (
              <div className="space-y-4">
                {checkins.slice(0, 5).map((checkin) => (
                  <div key={checkin.id} className="border-l-4 border-green-500 pl-4 py-2">
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="outline">Week {checkin.week_number}</Badge>
                      <span className="text-sm text-muted-foreground">
                        {new Date(checkin.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-sm">
                      {checkin.highlight && <><strong>Highlight:</strong> {checkin.highlight}<br/></>}
                      {checkin.learned && <><strong>Learned:</strong> {checkin.learned}<br/></>}
                      {checkin.mood && <><strong>Mood:</strong> {checkin.mood}<br/></>}
                      {checkin.what_learned && <><strong>Learned:</strong> {checkin.what_learned}<br/></>}
                      {checkin.most_enjoyed && <><strong>Enjoyed:</strong> {checkin.most_enjoyed}</>}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Calendar className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-30" />
                <p className="text-muted-foreground">No check-ins yet</p>
                <p className="text-sm text-muted-foreground mt-1">Complete your first weekly check-in!</p>
              </div>
            )}
          </Card>
        </div>
      </div>

      <Navigation role="youth" />
    </div>
  );
};

export default Profile;
